import { useContext, useEffect, useState } from 'react'
import { AgencyContext } from 'contexts'
import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import {
  AgxRow,
  AgxColumn,
  AgxToast,
  AgxSortableList,
} from '@urbanx/agx-ui-components'

import { v4 as uuidv4 } from 'uuid'

import './marketingItems.scss'
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation'

import { FormPrompt } from 'components/FormPrompt'
import { AgxToastState } from 'types/commonTypes'
import {
  AgxButton,
  AgxDropdown,
  AgxSaveChangesButton,
  AgxTextInput,
  AgxTypography,
  DropdownOption,
  AgxDivider,
  AgxSpacer,
  Icon_Arrow,
  Icon_CloseCircle,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2'
import {
  MarketingItemTemplateModel,
  MarketingItemTemplateModelItemType,
  useSetMarketingItemTemplates,
  MarketingItemTemplateModelCategory,
  useInvalidateUseStaffGetMarketingItemTemplates,
  MarketingItemTemplateModelTemplateType,
  MarketingItemTemplateBaseModel,
  MarketingItemTemplateBaseModelItemType,
} from '@urbanx/agx.marketing.client'
import { AgencySupplier } from '@urbanx/agx.agencies.client'
import { splitPascalCase } from 'utils/stringUtils'

interface EditItemsProps {
  onBack: () => void
  existingItems: MarketingItemTemplateModel[]
  agencyId: string
  agencySuppliers: AgencySupplier[]
}
type PackageItemKeys = keyof MarketingItemTemplateModel
const itemTypeOptions: DropdownOption[] = Object.entries(
  MarketingItemTemplateModelItemType
).map(([key, value]) => ({
  value: key,
  label: value,
}))

const categoryOptions: DropdownOption[] = Object.entries(
  MarketingItemTemplateModelCategory
).map(([key, value]) => ({
  value: key,
  label: splitPascalCase(value),
}))

const MarketingItems = ({
  onBack,
  existingItems,
  agencyId,
  agencySuppliers,
}: EditItemsProps) => {
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [forceCheck, setForceCheck] = useState(false)
  const [selectedAgency] = useContext(AgencyContext)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [saveChangesBtnState, setSaveChangesBtnState] =
    useState<SAVE_BUTTON_STATES>(SAVE_BUTTON_STATES.UNSAVED)
  const [selectedItemToDelete, setSelectedItemToDelete] = useState<
    MarketingItemTemplateModel | undefined
  >(undefined)
  const [items, setItems] = useState<MarketingItemTemplateModel[]>([])
  const invalidateMarketingItems =
    useInvalidateUseStaffGetMarketingItemTemplates()
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  })

  const { setMarketingItemTemplates } = useSetMarketingItemTemplates({
    onSuccess: async () => {
      setIsFormDirty(false)
      setSaveChangesBtnState(SAVE_BUTTON_STATES.SAVED)
      await invalidateMarketingItems(agencyId)
      updateToastState({
        color: 'success',
        message: 'Marketing package(s) updated successfully',
        open: true,
      })
    },
    onError: () => {
      setSaveChangesBtnState(SAVE_BUTTON_STATES.ERROR)
      updateToastState({
        color: 'error',
        message: 'Error updating Marketing package(s)',
        open: true,
      })
    },
  })

  const [errorList, setErrorList] = useState<string[]>([])

  useEffect(() => {
    if (existingItems) {
      setItems([...existingItems])
    }
  }, [existingItems])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      setItems((prev) => {
        const oldIndex = prev.findIndex((item) => item.id === active.id)
        const newIndex = prev.findIndex((item) => item.id === over?.id)
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }

  const onSaveHandler = () => {
    const errors = validateForm()
    if (errors.length === 0) {
      setErrorList([])
      setIsFormDirty(false)
      setSaveChangesBtnState(SAVE_BUTTON_STATES.SAVING)
      setMarketingItemTemplates({
        agencyId: selectedAgency?.id || '',
        marketingItemTemplates: sanatizeItems(),
      })
    } else {
      setErrorList(errors)
    }
  }

  const itemObjectUpdateHandler = (
    item: MarketingItemTemplateModel,
    value: string,
    prop: PackageItemKeys
  ) => {
    let defaultValue = ''

    let convertedValue = value === null || value === undefined ? '' : value
    if (prop === 'pricePerUnit') {
      defaultValue =
        item[prop] === null || item[prop] === undefined
          ? ''
          : parseFloat(`${item[prop]}`.replace(/,/g, '')).toString()
      convertedValue = convertedValue.replace(/,/g, '')
    } else {
      defaultValue =
        item[prop] === null || item[prop] === undefined
          ? ''
          : (item[prop] as string).toString()
    }

    if (defaultValue !== convertedValue) {
      setIsFormDirty(true)
    }

    if (item.templateType === MarketingItemTemplateModelTemplateType.Group) {
      const itemGroup = items.find((i) => {
        return (
          i.templateType === MarketingItemTemplateModelTemplateType.Group &&
          i.groupItems?.find((gi) => gi.id === item.id)
        )
      })

      if (itemGroup) {
        itemGroup.groupItems = itemGroup.groupItems?.map((i) => {
          return i.id === item.id
            ? ({ ...i, [prop]: value } as MarketingItemTemplateBaseModel)
            : i
        })

        setItems((oldItems) =>
          oldItems.map((oi) => {
            if (oi.id !== itemGroup.id) {
              return oi
            }

            return {
              ...itemGroup,
            }
          })
        )
        return
      }
    }

    setItems(
      items.map((i) => {
        return i.id === item.id ? { ...i, [prop]: value } : i
      })
    )
  }

  const sanatizeItems = () => {
    const sanatizedItems = items.map((item, index) => {
      const model = {
        category: item.category,
        description: item.description,
        id: item.id,
        itemType: item.itemType,
        order: item.order,
        pricePerUnit: item.pricePerUnit,
        productName: item.productName,
        supplierId: item.supplierId,
        templateType: item.templateType,
        groupItems: item.groupItems?.map((groupItem) => {
          return {
            ...groupItem,
          }
        }),
      }

      if (item.itemType !== MarketingItemTemplateModelItemType.Price) {
        return {
          ...model,
          pricePerUnit: undefined,
          order: index + 1,
        }
      }

      return {
        ...model,
        order: index + 1,
      }
    })

    return sanatizedItems
  }

  const validateForm = (): string[] => {
    const errors: string[] = []

    items.forEach((item) => {
      errors.push(...validateItem(item))

      if (item.groupItems) {
        item.groupItems.forEach((groupItem) => {
          errors.push(...validateItem(groupItem as MarketingItemTemplateModel))
        })
      }
    })

    // Return unique errors using Set
    return [...new Set(errors)]
  }

  const validateItem = (item: MarketingItemTemplateModel) => {
    const errors: string[] = []
    const itemPrices: Record<string, number | undefined> = {}

    if (item.productName?.trim() === '') {
      // Check for empty product names
      errors.push('Product name cannot be empty')
    }

    if (
      item.templateType === MarketingItemTemplateModelTemplateType.Group &&
      item.groupItems &&
      item.groupItems?.length > 0
    )
      return errors

    // Temporarily removed the mandatory check. This needs to be enabled later
    // Check for empty supplier
    // if (
    //   !item.supplierId ||
    //   supplierOptions.find((option) => option.value === item.supplierId) ===
    //     undefined
    // ) {
    //   errors.push('Supplier cannot be empty')
    // }

    // Temporarily removed the mandatory check. This needs to be enabled later
    // Check for empty category
    // if (!item.category) {
    //   errors.push('Category cannot be empty')
    // }
    // Check for empty price
    if (
      item.itemType === MarketingItemTemplateModelItemType.Price &&
      (!item.pricePerUnit || item.pricePerUnit <= 0)
    ) {
      errors.push('Price cannot be empty')
    }

    // Get the trimmed product name and price
    const productName = item.productName?.trim() ?? ''
    const price = item.pricePerUnit

    // Check for duplicate product names with the same price
    if (productName in itemPrices) {
      const storedPrice = itemPrices[productName]
      if (storedPrice === price) {
        errors.push(`Duplicate entry for product name ${productName}`)
      }
    } else {
      itemPrices[productName] = price
    }

    // Return unique errors using Set
    return errors
  }

  const onDeleteItemHandler = (item: MarketingItemTemplateModel) => {
    setSelectedItemToDelete(item)
    setShowDeleteConfirmation(true)
  }

  const onAddGroupItem = (item: MarketingItemTemplateModel) => {
    setItems((oldItems) => {
      return oldItems.map((oi) => {
        if (oi.id !== item.id) {
          return oi
        }

        const newGroupItem = {
          order: oi.groupItems?.length ?? 0 + 1,
          productName: '',
          id: `${uuidv4()}`,
          itemType: MarketingItemTemplateBaseModelItemType.Price,
        }

        return {
          ...oi,
          groupItems: [newGroupItem, ...(oi.groupItems ?? [])],
        }
      })
    })
  }

  const onConfirmDelete = () => {
    if (selectedItemToDelete) {
      setIsFormDirty(true)
      if (
        selectedItemToDelete.templateType ===
        MarketingItemTemplateModelTemplateType.Group
      ) {
        const itemGroup = items.find(
          (item) =>
            item.templateType ===
              MarketingItemTemplateModelTemplateType.Group &&
            item.groupItems?.find((gi) => gi.id === selectedItemToDelete.id)
        )

        if (itemGroup) {
          itemGroup.groupItems = itemGroup.groupItems?.filter(
            (gi) => gi.id !== selectedItemToDelete.id
          )
          setItems((oldItems) =>
            oldItems.map((oi) => {
              if (oi.id !== itemGroup.id) {
                return oi
              }

              return {
                ...itemGroup,
              }
            })
          )

          setShowDeleteConfirmation(false)
          return
        }
      }
      setItems(items.filter((i) => i.id !== selectedItemToDelete.id))
      setShowDeleteConfirmation(false)
    }
  }

  const onBackClick = () => {
    if (isFormDirty) setForceCheck(true)
    else onBack()
  }

  const [supplierOptions, setSupplierOptions] = useState<DropdownOption[]>([])

  useEffect(() => {
    if (agencySuppliers) {
      setSupplierOptions(
        [...agencySuppliers]
          .sort((a, b) =>
            (a.companyName || '').localeCompare(b.companyName || '')
          )
          .map((s) => ({
            value: s.supplierId || '',
            label: s.companyName || '',
          }))
      )
    }
  }, [agencySuppliers])

  const renderItems = (
    item: MarketingItemTemplateModel,
    index: number,
    totalItems: number
  ) => {
    const { templateType } = item
    switch (templateType) {
      case MarketingItemTemplateModelTemplateType.Item: {
        return renderItem(item, index, totalItems)
      }
      case MarketingItemTemplateModelTemplateType.Group: {
        return renderGroupItem(item, index)
      }
      default: {
        return renderItem(item, index, totalItems)
      }
    }
  }

  const renderGroupItem = (item: MarketingItemTemplateModel, index: number) => {
    const { groupItems } = item
    const total = groupItems?.reduce((acc, curr) => {
      if (curr.pricePerUnit) {
        acc = acc + parseFloat(`${curr.pricePerUnit}`.replace(/,/g, ''))
      }

      return acc
    }, 0)

    return (
      <AgxSpacer fill direction="vertical" className="groupedItem" gap={'xs'}>
        <AgxSpacer
          direction="horizontal"
          fill
          contentJustification="space-between"
        >
          <div className="column-groupName">
            <AgxTextInput
              type="text"
              placeholder="Group name"
              hideOptionalText={true}
              id={`txtGroupProductName-${index}`}
              noHeader
              stretch
              errorText={
                errorList.length > 0 && !item.productName?.trim()
                  ? 'Product group name cannot be empty'
                  : ''
              }
              hideErrorDescription
              // Autofocus the top newly added item name field
              autoFocus={items[0].productName === ''}
              defaultValue={item.productName}
              onInputValueChange={({ value }: { value: string }) =>
                itemObjectUpdateHandler(item, value, 'productName')
              }
            />
          </div>
          <div className="column-groupPrice">
            <AgxTextInput
              type="currency"
              hideOptionalText={true}
              id={`txtProductName-${index}`}
              noHeader
              disabled
              hideErrorDescription
              // Autofocus the top newly added item name field
              autoFocus={items[0].productName === ''}
              defaultValue={`${total}`}
            />
          </div>
        </AgxSpacer>
        <div className="groupItem-divider-stretch">
          <AgxDivider variant="horizontal" />
        </div>
        {groupItems?.map((groupItem, index) =>
          renderItem(
            {
              templateType: MarketingItemTemplateModelTemplateType.Group, // this is needed to render different options for price
              groupItems: undefined,
              id: groupItem.id,
              order: groupItem.order,
              productName: groupItem.productName,
              description: groupItem.description,
              pricePerUnit: groupItem.pricePerUnit,
              itemType:
                groupItem.itemType as unknown as MarketingItemTemplateModelItemType,
              supplierId: groupItem.supplierId,
              category:
                groupItem.category as unknown as MarketingItemTemplateModelCategory,
            },
            index,
            groupItems.length
          )
        )}
        <AgxSpacer
          contentJustification="end"
          gap={'sm'}
          fill
          direction="horizontal"
        >
          <AgxButton
            id="btnAddGroupItem"
            text="Add Item"
            variant="hollow"
            size="small"
            onClick={() => onAddGroupItem(item)}
          />
          <AgxButton
            id="btnAddGroupItem"
            text="Delete Group"
            variant="hollow"
            danger
            size="small"
            onClick={() => onDeleteItemHandler(item)}
          />
        </AgxSpacer>
      </AgxSpacer>
    )
  }

  const renderItem = (
    item: MarketingItemTemplateModel,
    index: number,
    totalItems: number
  ) => {
    return (
      <AgxSpacer
        key={item.id}
        contentAlignment="center"
        contentJustification="space-between"
        gap={'xs'}
        direction="horizontal"
        fill
        id={item.id || `dragItem-${index}`}
        className={'marketingPackageItem'}
      >
        <AgxRow centered veryLargeGap fill>
          <div
            className={
              item.templateType === MarketingItemTemplateModelTemplateType.Group
                ? 'column-groupNameItem'
                : 'column-item'
            }
          >
            <AgxTextInput
              type="text"
              hideOptionalText={true}
              id={`txtProductName-${item.id}`}
              placeholder="Item name"
              noHeader
              stretch
              errorText={
                errorList.length > 0 && !item.productName?.trim()
                  ? 'Product name cannot be empty'
                  : ''
              }
              hideErrorDescription
              // Autofocus the top newly added item name field
              autoFocus={items[0].productName === ''}
              defaultValue={item.productName}
              onInputValueChange={({ value }: { value: string }) =>
                itemObjectUpdateHandler(item, value, 'productName')
              }
            />
          </div>
          <div className="column-supplier">
            <AgxDropdown
              id={`txtSupplier-${item.id}`}
              options={supplierOptions}
              hideOptionalText
              noHeader
              onChange={(value: string) =>
                itemObjectUpdateHandler(item, value, 'supplierId')
              }
              placeholder="Select supplier"
              // Temporarily removed the mandatory check. This needs to be enabled later
              // errorText={
              //   errorList.length > 0 &&
              //   (!item.supplierId ||
              //     supplierOptions.find(
              //       (option) => option.value === item.supplierId
              //     ) === undefined)
              //     ? 'Supplier cannot be empty'
              //     : ''
              // }
              hideErrorDescription
              selected={
                supplierOptions.find(
                  (option) => option.value === item.supplierId
                )?.value
              }
            />
          </div>
          <div className="column-category">
            <AgxDropdown
              id={`txtCategory-${item.id}`}
              options={categoryOptions}
              noHeader
              onChange={(value: string) =>
                itemObjectUpdateHandler(item, value, 'category')
              }
              placeholder="Select category"
              // Temporarily removed the mandatory check. This needs to be enabled later
              // errorText={
              //   errorList.length > 0 && !item.category
              //     ? 'Category cannot be empty'
              //     : ''
              // }
              hideErrorDescription
              selected={
                categoryOptions.find((option) => option.value === item.category)
                  ?.value ?? categoryOptions[0].value
              }
            />
          </div>
          <div className="column-description">
            <AgxTextInput
              id={`txtDescription-${item.id}`}
              noHeader
              type="text"
              stretch
              defaultValue={item.description ?? ''}
              onInputValueChange={({ value }: { value: string }) =>
                itemObjectUpdateHandler(item, value, 'description')
              }
            />
          </div>

          <div
            className={
              item.templateType === MarketingItemTemplateModelTemplateType.Group
                ? 'column-groupItemPrice'
                : 'column-price'
            }
          >
            <AgxTextInput
              id={`priceAmount-${item.id}`}
              type={
                item.itemType !== MarketingItemTemplateModelItemType.Price
                  ? 'text'
                  : 'currency'
              }
              required
              noHeader
              stretch
              placeholder="0.00"
              readonly={
                item.itemType !== MarketingItemTemplateModelItemType.Price
              }
              variant="withOptions"
              options={
                item.templateType ===
                MarketingItemTemplateModelTemplateType.Group
                  ? itemTypeOptions.filter(
                      (f) =>
                        f.label !==
                        MarketingItemTemplateModelItemType.AgentToFill
                    )
                  : itemTypeOptions
              }
              selectedOption={item.itemType}
              onOptionChange={(value: string) =>
                itemObjectUpdateHandler(item, value, 'itemType')
              }
              defaultValue={`${item.itemType === MarketingItemTemplateModelItemType.Price ? (item.pricePerUnit ?? '') : item.itemType}`}
              onInputValueChange={(change) => {
                if (
                  item.itemType === MarketingItemTemplateModelItemType.Price
                ) {
                  itemObjectUpdateHandler(
                    item,
                    change.value as string,
                    'pricePerUnit'
                  )
                }
              }}
              errorText={
                errorList.length > 0 &&
                item.itemType === MarketingItemTemplateModelItemType.Price &&
                (!item.pricePerUnit || item.pricePerUnit <= 0)
                  ? 'Price cannot be empty'
                  : ''
              }
              hideErrorDescription
            />
          </div>
        </AgxRow>
        {item.templateType === MarketingItemTemplateModelTemplateType.Group &&
          totalItems > 1 && (
            <div
              className="actionContainerTD"
              onClick={() => onDeleteItemHandler(item)}
            >
              <Icon_CloseCircle />
            </div>
          )}
      </AgxSpacer>
    )
  }

  const renderSortableContainer = () => {
    return (
      <AgxSpacer
        direction="horizontal"
        contentAlignment="center"
        contentJustification="space-between"
        fill
        padding={{
          top: '2xs',
          right: '2xs',
          bottom: '2xs',
          left: 'spacing-5xl',
        }}
      >
        <AgxSpacer fill contentAlignment="center" direction="horizontal">
          <div className="column-item-header">
            <AgxTypography
              id={'lblInclusionName'}
              type={'label'}
              size={'medium'}
            >
              Item
            </AgxTypography>
          </div>
          <div className="column-supplier-header">
            <AgxTypography
              id={'lblInclusionName'}
              type={'label'}
              size={'medium'}
            >
              Supplier
            </AgxTypography>
          </div>
          <div className="column-category">
            <AgxTypography
              id={'lblInclusionName'}
              type={'label'}
              size={'medium'}
            >
              Category
            </AgxTypography>
          </div>
          <div className="column-description-header">
            <AgxTypography
              id={'lblInclusionName'}
              type={'label'}
              size={'medium'}
            >
              Description
            </AgxTypography>
            <AgxTypography
              id={`optionalText`}
              type={'caption'}
              size={'medium'}
              color="var(--text-tertiary)"
            >
              {` (Optional)`}
            </AgxTypography>
          </div>
          <div className="column-price">
            <AgxTypography
              id={'lblInclusionName'}
              type={'label'}
              size={'medium'}
            >
              Price
            </AgxTypography>
          </div>
        </AgxSpacer>
      </AgxSpacer>
    )
  }

  return (
    <AgxRow fill>
      <FormPrompt
        hasUnsavedChanges={isFormDirty}
        forceCheck={forceCheck}
        resetForceCheck={(value: boolean) => setForceCheck(value)}
        onBack={onBack}
      />
      <AgxToast selector="#agxToast" toastState={toastState} />
      <AgxColumn fill>
        <AgxRow veryLargeGap spaceBetween extraClasses="borderBottomContainer">
          <div onClick={() => onBackClick()} className="backbtnStyle">
            <Icon_Arrow direction="left" />
            <AgxTypography id={'txtBack'} type={'body_ui'} size={'small'}>
              Marketing Package
            </AgxTypography>
          </div>
          {/* <AgxButton
            id="btnMarketingPkg"
            text="Marketing Package"
            size="small"
            variant="naked"
            onClick={() => onBackClick()}
          /> */}
          <AgxRow largeGap>
            <AgxButton
              id="btnAddItem"
              text="Add Item"
              size="small"
              variant="hollow"
              onClick={() => {
                setIsFormDirty(true)
                const newItem = {
                  productName: '',
                  templateType: MarketingItemTemplateModelTemplateType.Item,
                  itemType: MarketingItemTemplateModelItemType.Price,
                  id: `${uuidv4()}`,
                }
                setItems([newItem, ...items])
              }}
            />
            <AgxButton
              id="btnAddGroup"
              text="Add Group"
              size="small"
              variant="hollow"
              onClick={() => {
                setIsFormDirty(true)
                const newItem = {
                  productName: '',
                  templateType: MarketingItemTemplateModelTemplateType.Group,
                  groupItems: [
                    {
                      order: 1,
                      productName: '',
                      id: `${uuidv4()}`,
                      itemType: MarketingItemTemplateBaseModelItemType.Price,
                    },
                  ],
                  itemType: MarketingItemTemplateModelItemType.Price,
                  id: `${uuidv4()}`,
                }
                setItems([newItem, ...items])
              }}
            />
            <AgxSaveChangesButton
              state={
                isFormDirty ? SAVE_BUTTON_STATES.UNSAVED : saveChangesBtnState
              }
              id="btnSaveChanges"
              text="Save Changes"
              size="small"
              variant="primary"
              onClick={() => onSaveHandler()}
              disabled={!isFormDirty}
            />
          </AgxRow>
        </AgxRow>
        <AgxSortableList
          items={items}
          getId={(item) => item.id || `editItem-${item.id}`}
          renderSortableContextChildren={renderSortableContainer}
          renderItem={(item, index) => renderItems(item, index, items.length)}
          onDragEnd={handleDragEnd}
          extraClasses="marketingPackageListItems"
        />
      </AgxColumn>
      <DeleteConfirmation
        name={selectedItemToDelete?.productName || ''}
        title="Delete marketing item?"
        lineOne="Are you sure you want to delete"
        lineTwo="This cannot be undone and will remove the item from any marketing packages that it has been added to."
        primaryActionTitle="Permanently delete item"
        secondaryActionTitle="No, cancel"
        showPopUp={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => onConfirmDelete()}
      />
    </AgxRow>
  )
}

export default MarketingItems
